// Discover-Onboarding überspringen, nur für Aivy-Admins und nur in diesem
// Browser. Der echte Onboarding-Status steckt in customer_dashboard_meta des
// eigenen Space des Admins und hat mit dem Kunden nichts zu tun. Dieser Skip
// schreibt nichts in die DB, er wirkt nur über den UserContext.
//
// localStorage DISCOVER_ONBOARDING_ADMIN_SKIP = '1'
// Gilt für alle Kunden, weil der Status ohnehin am Admin-Space hängt und nicht
// am Partner. signOut leert localStorage und damit auch das.
//
// Der Key steht hier und nicht in LOCALSTORAGE_ITEMS, weil helpers/index.js
// aws-amplify zieht und Jest bricht.
import { useCallback, useSyncExternalStore } from 'react'

export const DISCOVER_ONBOARDING_ADMIN_SKIP = 'DISCOVER_ONBOARDING_ADMIN_SKIP'

const SKIPPED_VALUE = '1'

const readRaw = () => {
  try {
    return window.localStorage.getItem(DISCOVER_ONBOARDING_ADMIN_SKIP)
  } catch {
    return null
  }
}

/** Hat der Admin in diesem Browser das Onboarding übersprungen? */
export const readAdminOnboardingSkip = () => readRaw() === SKIPPED_VALUE

const listeners = new Set()

const emit = () => listeners.forEach((listener) => listener())

const subscribe = (listener) => {
  listeners.add(listener)
  // storage feuert nur in anderen Tabs. key null heißt localStorage.clear().
  const onStorage = (event) => {
    if (event.key === null || event.key === DISCOVER_ONBOARDING_ADMIN_SKIP) {
      listener()
    }
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

export const setAdminOnboardingSkip = (skipped) => {
  try {
    if (skipped) {
      window.localStorage.setItem(DISCOVER_ONBOARDING_ADMIN_SKIP, SKIPPED_VALUE)
    } else {
      window.localStorage.removeItem(DISCOVER_ONBOARDING_ADMIN_SKIP)
    }
  } catch {
    // Ohne localStorage bleibt das Onboarding sichtbar
  }
  emit()
}

const subscribeNothing = () => () => {}
const readFalse = () => false

/**
 * Skip-Zustand für den aktuellen Nutzer. Ohne Admin wird localStorage nicht
 * gelesen, `skipped` ist dann immer false und die Setter tun nichts.
 *
 * @param {{ isAdmin: boolean }} params
 * @returns {{ skipped: boolean, skip(): void, reset(): void }}
 */
export const useAdminOnboardingSkip = ({ isAdmin } = {}) => {
  const available = !!isAdmin

  const skipped = useSyncExternalStore(
    available ? subscribe : subscribeNothing,
    available ? readAdminOnboardingSkip : readFalse,
    readFalse
  )

  const skip = useCallback(() => {
    if (available) setAdminOnboardingSkip(true)
  }, [available])

  const reset = useCallback(() => {
    if (available) setAdminOnboardingSkip(false)
  }, [available])

  return { skipped, skip, reset }
}
